import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import axios from 'axios';
import { Users, TrendingUp, Trophy, Coins, FileText, Bell, Send, ExternalLink, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [revenueStreams, setRevenueStreams] = useState(null);
  const [affiliateClicks, setAffiliateClicks] = useState([]);
  const [b2bLeads, setB2bLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMessage, setNotifMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/stats`);
      setStats(data);
    } catch (err) {
      toast.error('Failed to load stats');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`);
      setUsers(data);
    } catch (err) {
      toast.error('Failed to load users');
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/transactions`);
      setTransactions(data);
    } catch (err) {
      toast.error('Failed to load transactions');
    }
  }, []);

  const fetchRevenueStreams = useCallback(async () => {
    try {
      const [s, c, l] = await Promise.all([
        axios.get(`${API_URL}/api/admin/revenue-streams`),
        axios.get(`${API_URL}/api/admin/affiliate-clicks`),
        axios.get(`${API_URL}/api/admin/leads`),
      ]);
      setRevenueStreams(s.data);
      setAffiliateClicks(c.data);
      setB2bLeads(l.data);
    } catch (err) {
      toast.error('Failed to load revenue streams');
    }
  }, []);

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.post(`${API_URL}/api/admin/leads/${leadId}/status`, { status });
      toast.success('Lead status updated');
      fetchRevenueStreams();
    } catch (err) {
      toast.error('Failed to update lead');
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'transactions') fetchTransactions();
    if (activeTab === 'revenue') fetchRevenueStreams();
  }, [activeTab, fetchUsers, fetchTransactions, fetchRevenueStreams]);

  const sendNotification = async () => {
    if (!notifTitle.trim() || !notifMessage.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/admin/notifications`, {
        title: notifTitle,
        message: notifMessage,
        type: 'info'
      });
      toast.success('Notification sent to all users!');
      setNotifTitle('');
      setNotifMessage('');
    } catch (err) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'revenue', label: 'Revenue Streams', icon: Briefcase },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'transactions', label: 'Transactions', icon: FileText },
    { id: 'notifications', label: 'Send Notification', icon: Bell },
  ];

  const statCards = stats ? [
    { label: 'Total Users', value: stats.total_users, icon: Users, color: '#0EA5E9' },
    { label: 'Active Pools', value: stats.active_pools, icon: Trophy, color: '#FBBF24' },
    { label: 'Total Revenue', value: `${stats.total_revenue}`, icon: Coins, color: '#10B981' },
    { label: 'Prizes Given', value: `${stats.total_prizes_distributed}`, icon: TrendingUp, color: '#FF6B6B' },
    { label: 'Quizzes Taken', value: stats.total_attempts, icon: FileText, color: '#8B5CF6' },
    { label: 'Signups (7d)', value: stats.recent_signups, icon: Users, color: '#F59E0B' },
  ] : [];

  const formatType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-black text-[#111827] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="admin-title">
          Admin Dashboard
        </h1>
        <p className="text-[#6B7280] mb-8">Manage your platform, users, and analytics.</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="admin-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-[#0EA5E9] text-white shadow-md'
                  : 'bg-white text-[#6B7280] hover:bg-slate-100 border border-slate-200'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {!stats ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0EA5E9]"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="admin-stats-grid">
                {statCards.map((card, idx) => (
                  <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid={`admin-stat-${card.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${card.color}15` }}>
                        <card.icon className="w-6 h-6" style={{ color: card.color }} />
                      </div>
                      <div>
                        <p className="text-sm text-[#6B7280]">{card.label}</p>
                        <p className="text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{card.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Revenue Streams Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-8" data-testid="admin-revenue-tab">
            {!revenueStreams ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0EA5E9]"></div>
              </div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  <SummaryCard label="Affiliate clicks (total)" value={revenueStreams.affiliate.total_clicks} color="#10B981" Icon={ExternalLink} />
                  <SummaryCard label="Affiliate clicks (7 days)" value={revenueStreams.affiliate.clicks_7d} color="#0EA5E9" Icon={TrendingUp} />
                  <SummaryCard label="B2B leads (total)" value={revenueStreams.b2b_leads.total} color="#F59E0B" Icon={Briefcase} />
                  <SummaryCard label="New leads to action" value={revenueStreams.b2b_leads.new} color="#FF6B6B" Icon={Bell} />
                </div>

                {/* Top affiliate rewards */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-testid="admin-top-affiliate">
                  <h3 className="text-lg font-bold text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Top affiliate rewards
                  </h3>
                  {(revenueStreams.affiliate.top_rewards || []).length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] py-4">No clicks yet. Share your /rewards page to get started.</p>
                  ) : (
                    <div className="space-y-2">
                      {revenueStreams.affiliate.top_rewards.map((r, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
                          <div>
                            <p className="font-bold text-[#111827] text-sm">{r.reward_name}</p>
                            <p className="text-xs text-[#6B7280]">{r.brand}</p>
                          </div>
                          <span className="text-lg font-black text-emerald-600" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                            {r.clicks} clicks
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent affiliate clicks */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admin-affiliate-clicks">
                  <div className="p-6 border-b border-slate-100">
                    <h3 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Recent affiliate clicks
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Brand</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Reward</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {affiliateClicks.slice(0, 30).map((c, idx) => (
                          <tr key={idx} className="border-b border-slate-50">
                            <td className="px-6 py-3 text-sm font-bold text-[#111827]">{c.brand}</td>
                            <td className="px-6 py-3 text-sm text-[#6B7280]">{c.reward_name}</td>
                            <td className="px-6 py-3 text-sm text-[#9CA3AF]">{new Date(c.clicked_at).toLocaleString()}</td>
                          </tr>
                        ))}
                        {affiliateClicks.length === 0 && (
                          <tr><td colSpan={3} className="px-6 py-12 text-center text-[#9CA3AF]">No clicks yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* B2B Leads */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admin-b2b-leads">
                  <div className="p-6 border-b border-slate-100">
                    <h3 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      B2B leads (institute &amp; partnership)
                    </h3>
                    <p className="text-sm text-[#6B7280] mt-1">From <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">/for-institutes</code> form</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Contact</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Institute</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Interest</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Students</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Status</th>
                          <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {b2bLeads.map((l, idx) => (
                          <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`b2b-lead-row-${idx}`}>
                            <td className="px-6 py-3 text-sm">
                              <p className="font-bold text-[#111827]">{l.name}</p>
                              <p className="text-xs text-[#6B7280]">{l.email}</p>
                              {l.role && <p className="text-xs text-[#9CA3AF]">{l.role}</p>}
                            </td>
                            <td className="px-6 py-3 text-sm text-[#111827]">{l.institute}</td>
                            <td className="px-6 py-3 text-sm">
                              <span className="px-2 py-1 rounded-lg text-xs font-bold bg-amber-100 text-amber-700">{(l.interest || '').replace(/_/g, ' ')}</span>
                            </td>
                            <td className="px-6 py-3 text-sm text-[#6B7280]">{l.students_count || '—'}</td>
                            <td className="px-6 py-3">
                              <select
                                value={l.status}
                                onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                                className="text-xs font-bold px-2 py-1 border border-slate-200 rounded-lg focus:outline-none"
                                data-testid={`b2b-lead-status-${idx}`}
                              >
                                <option value="new">New</option>
                                <option value="contacted">Contacted</option>
                                <option value="qualified">Qualified</option>
                                <option value="won">Won</option>
                                <option value="lost">Lost</option>
                              </select>
                            </td>
                            <td className="px-6 py-3 text-xs text-[#9CA3AF]">{new Date(l.created_at).toLocaleString()}</td>
                          </tr>
                        ))}
                        {b2bLeads.length === 0 && (
                          <tr><td colSpan={6} className="px-6 py-12 text-center text-[#9CA3AF]">No leads yet. Share /for-institutes link with coaching contacts.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admin-users-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Name</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Email</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Points</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Role</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Auth</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Quizzes</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`admin-user-row-${idx}`}>
                      <td className="px-6 py-4 text-sm font-medium text-[#111827]">{u.name}</td>
                      <td className="px-6 py-4 text-sm text-[#6B7280]">{u.email}</td>
                      <td className="px-6 py-4 text-sm font-bold text-[#0EA5E9]">{u.points}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-lg text-xs font-bold ${u.role === 'admin' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B7280]">{u.auth_provider}</td>
                      <td className="px-6 py-4 text-sm text-[#111827]">{u.quizzes_played}</td>
                      <td className="px-6 py-4 text-sm text-[#6B7280]">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-12 text-center text-[#6B7280]">No users found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="admin-transactions-table">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">User</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Type</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Amount</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Status</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-[#6B7280] uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn, idx) => (
                    <tr key={idx} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`admin-txn-row-${idx}`}>
                      <td className="px-6 py-4 text-sm font-medium text-[#111827]">{txn.user_name}</td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-1 rounded-lg text-xs font-bold bg-slate-100 text-slate-600">{formatType(txn.type)}</span>
                      </td>
                      <td className={`px-6 py-4 text-sm font-bold ${txn.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {txn.amount >= 0 ? '+' : ''}{txn.amount} pts
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6B7280]">{txn.status || '-'}</td>
                      <td className="px-6 py-4 text-sm text-[#6B7280]">{new Date(txn.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {transactions.length === 0 && (
                    <tr><td colSpan={5} className="px-6 py-12 text-center text-[#6B7280]">No transactions found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Send Notification Tab */}
        {activeTab === 'notifications' && (
          <div className="max-w-lg" data-testid="admin-send-notification">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="text-lg font-bold text-[#111827] mb-4">Broadcast Notification</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-[#111827] mb-1">Title</label>
                  <input
                    type="text"
                    value={notifTitle}
                    onChange={e => setNotifTitle(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors"
                    placeholder="e.g. New Mega Contest!"
                    data-testid="notif-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#111827] mb-1">Message</label>
                  <textarea
                    value={notifMessage}
                    onChange={e => setNotifMessage(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-[#E5E7EB] rounded-xl focus:border-[#0EA5E9] focus:outline-none transition-colors resize-none"
                    rows={4}
                    placeholder="Write the notification message..."
                    data-testid="notif-message-input"
                  />
                </div>
                <button
                  onClick={sendNotification}
                  disabled={sending}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                  data-testid="send-notification-button"
                >
                  <Send className="w-4 h-4" />
                  {sending ? 'Sending...' : 'Send to All Users'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;

// eslint-disable-next-line react/prop-types
const SummaryCard = ({ label, value, color, Icon }) => (
  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-xs text-[#6B7280]">{label}</p>
        <p className="text-2xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>{value}</p>
      </div>
    </div>
  </div>
);
